import React, {
    useCallback,
    useEffect,
    useLayoutEffect,
    useRef,
    useState,
} from 'react';
import './press-game.css';
import {
    goToScreen,
    goToScreenAndReset,
    setHistoryScreenState,
} from '../../actions/router';
import { connect } from 'react-redux';
import {
    MAIN_GAME_SCREEN,
    PRESS_GAME_SCREEN,
    SETTINGS_SCREEN,
} from '../../constants/routes';
import { completeTutorial } from '../../actions/tutorials';
import { progressTime, updateOverallScores } from '../../actions/active-game';
import { IconButton } from '../../components/icon-button';
import { TutorialModalParent } from '../../components/modal-parents/tutorial-modal';
import { PressAnimation } from '../../animations/press-animation';
import { SubjectSelector } from './subject-selector';
import classNames from 'classnames';
import Copy from '../../copy/press';
import { Journalist } from './journalist';
import { ProgressUpdateModalParent } from '../../components/modal-parents/progress-update-modal';
import { selectRandomArrayEntry } from '../../helpers/active-game';
import { GameSettingsModalParent } from '../../components/modal-parents/game-settings-modal';
import { Timer } from '../../components/timer';
import { Paragraph } from '../../components/paragraph';
import { PerformanceTracker } from '../../components/performance-tracker';
import { TextButton } from '../../components/text-button';
const subjects = [
    'climate',
    'crime',
    'employment',
    'arts',
    'world-issues',
    'education',
    'sports',
];
const journalistsArray = [0, 1, 2, 3, 4, 5, 6, 7, 8];

const PressGameScreen = props => {
    const {
        goToSettings,
        continueTo,
        continueRoute,
        historyScreenState,
        tutorialCompleted,
        completePressTutorial,
        previousScores,
        submitOverallScores,
        timedGames,
        progressGame,
    } = props;

    const pressAnimation = useRef(new PressAnimation());
    const gameTimerRef = useRef(null);

    const [settingsOptionChosen, setSettingsOptionChosen] = useState('1 min');
    const [settingsChosen, setSettingsChosen] = useState(false);
    const [progressUpdateVisible, setProgressUpdateVisible] = useState(false);

    const [currentGameScores, setCurrentGameScores] = useState({});
    const [responseFeedback, setResponseFeedback] = useState(null);

    const [noTimer, setNoTimer] = useState(false);
    const [timerLength, setTimerLength] = useState(60);

    const [gameTimer, setGameTimer] = useState(null);
    const [currentPerformance, setCurrentPerformance] = useState(0);

    const [activeSubjectIndex, setActiveSubjectIndex] = useState(0);
    const [activeJournalists, setActiveJournalists] = useState([]);

    const modalVisible =
        progressUpdateVisible || !tutorialCompleted || !settingsChosen;

    useEffect(() => {
        if (!historyScreenState.state.timeLeft) {
            const seconds =
                settingsOptionChosen === 'No limit'
                    ? 0
                    : settingsOptionChosen === '2 min'
                    ? 120
                    : 60;

            if (seconds === 0) {
                setNoTimer(true);
            } else {
                setNoTimer(false);
            }
            setGameTimer(seconds);
            setTimerLength(seconds);
        }
    }, [historyScreenState.state.timeLeft, settingsOptionChosen]);

    const initialiseCanvas = useCallback(ref => {
        if (ref !== null) {
            pressAnimation.current.initialise('pressCanvas');
        }
    }, []);

    useLayoutEffect(() => {
        if (historyScreenState.screen === PRESS_GAME_SCREEN) {
            setCurrentPerformance(historyScreenState.state.currentPerformance);
            setActiveJournalists(historyScreenState.state.activeJournalists);
            setSettingsChosen(historyScreenState.state.settingsChosen);
            setTimerLength(historyScreenState.state.timerLength);
            if (historyScreenState.state.noTimer === true) {
                setNoTimer(true);
                setGameTimer(0);
                setSettingsOptionChosen('No limit');
            } else {
                setGameTimer(historyScreenState.state.timeLeft);
            }
        }
    }, [historyScreenState]);

    useEffect(() => {
        if (!timedGames) {
            setNoTimer(true);
            setSettingsChosen(true);
            setGameTimer(0);
        }
    }, [timedGames]);

    useEffect(() => {
        if (
            tutorialCompleted &&
            settingsChosen &&
            noTimer === false &&
            gameTimer === timerLength
        ) {
            gameTimerRef.current = setInterval(() => {
                setGameTimer(timer => timer - 1);
                setActiveJournalists(activeJournalists => [
                    ...activeJournalists,
                    selectRandomArrayEntry(journalistsArray),
                ]);
            }, 1000);
        } else if (
            gameTimer === historyScreenState.state.timeLeft &&
            historyScreenState.state.noTimer === false
        ) {
            gameTimerRef.current = setInterval(() => {
                setGameTimer(timer => timer - 1);
                setActiveJournalists(activeJournalists => [
                    ...activeJournalists,
                    selectRandomArrayEntry(journalistsArray),
                ]);
            }, 1000);
        } else if (gameTimer === 0) {
            if (noTimer === false) {
                clearInterval(gameTimerRef.current);
                finishPressConference();
            } else if (currentPerformance === 0) {
                gameTimerRef.current = setInterval(() => {
                    setActiveJournalists(activeJournalists => [
                        ...activeJournalists,
                        selectRandomArrayEntry(journalistsArray),
                    ]);
                }, 2000);
            }
            return () => {
                clearInterval(gameTimerRef.current);
            };
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [
        gameTimer,
        settingsChosen,
        historyScreenState.state.timeLeft,
        tutorialCompleted,
    ]);

    const journalistSit = journalist => {
        pressAnimation.current.journalistSit(journalist);
        setActiveJournalists(
            activeJournalists.filter(
                activeJournalist => activeJournalist !== journalist,
            ),
        );
    };

    const finishPressConference = () => {
        const performance =
            currentPerformance < 5
                ? 'poor'
                : currentPerformance > 19
                ? 'good'
                : 'medium';

        const results = Copy.results[performance];

        activeJournalists.forEach(journalist => journalistSit(journalist));
        setActiveJournalists([]);
        setCurrentGameScores(results.scores);
        setResponseFeedback(results.feedback);
        setProgressUpdateVisible(true);
    };

    useEffect(() => {
        if (noTimer === true && currentPerformance === 20) {
            clearInterval(gameTimerRef.current);
            finishPressConference();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [noTimer, currentPerformance]);

    const completeGame = () => {
        if (continueRoute === MAIN_GAME_SCREEN) {
            submitOverallScores(previousScores, currentGameScores);
            progressGame();
        }
        continueTo(continueRoute);
    };

    const pressGameClasses = classNames({
        'press-game': true,
        'press-game--hidden': modalVisible,
    });

    const uniqueActiveJournalists = activeJournalists.filter(
        (journalist, index, array) => array.indexOf(journalist) === index,
    );

    return (
        <>
            <main
                aria-label="press game"
                className={pressGameClasses}
                aria-hidden={modalVisible}
            >
                <div className="press-game__buttons" aria-label="Press Game">
                    <div className="press-game__buttons-back">
                        <IconButton
                            iconName="back"
                            size="small"
                            type="essential"
                            withSpacing={true}
                            onClick={() => {
                                clearInterval(gameTimerRef.current);
                                continueTo(continueRoute);
                                pressAnimation.current.destroy();
                            }}
                            isDisabled={modalVisible}
                        />
                    </div>
                    <div className="press-game__buttons-settings">
                        <IconButton
                            iconName="settings"
                            size="small"
                            type="essential"
                            withSpacing={true}
                            onClick={() => {
                                clearInterval(gameTimerRef.current);
                                goToSettings(
                                    currentPerformance,
                                    gameTimer,
                                    noTimer,
                                    activeJournalists,
                                    settingsChosen,
                                    timerLength,
                                );
                            }}
                            isDisabled={modalVisible}
                        />
                    </div>
                    {Array.from(Array(9), (placeholder, i) => {
                        const index = 8 - i;
                        return (
                            <Journalist
                                key={index}
                                index={index}
                                journalistIsActive={uniqueActiveJournalists.includes(
                                    index,
                                )}
                                subjects={subjects}
                                activeSubject={subjects[activeSubjectIndex]}
                                noTimer={noTimer}
                                sit={() => journalistSit(index)}
                                stand={() =>
                                    pressAnimation.current.journalistStand(
                                        index,
                                    )
                                }
                                makeAngry={() =>
                                    pressAnimation.current.makeJournalistAngry(
                                        index,
                                    )
                                }
                                makeHappy={() =>
                                    pressAnimation.current.makeJournalistHappy(
                                        index,
                                    )
                                }
                                satisfyJournalist={() => {
                                    setCurrentPerformance(
                                        currentPerformance + 1,
                                    );
                                }}
                            />
                        );
                    })}
                    {tutorialCompleted && settingsChosen && !noTimer ? (
                        <div className="press-game__timer">
                            <Timer
                                timeElapsed={gameTimer}
                                maxTime={timerLength}
                            />
                            <Paragraph fontWeight="bold" withSpacing={false}>
                                Timer
                            </Paragraph>
                        </div>
                    ) : (
                        <div className="press-game__finish-button">
                            <TextButton
                                onClick={() => {
                                    clearInterval(gameTimerRef.current);
                                    finishPressConference();
                                    pressAnimation.current.destroy();
                                }}
                                text="Finish"
                                isDisabled={modalVisible}
                                pressGame
                            />
                        </div>
                    )}
                    <SubjectSelector
                        activeSubject={subjects[activeSubjectIndex]}
                        activeSubjectIndex={activeSubjectIndex}
                        setActiveSubjectIndex={setActiveSubjectIndex}
                        modalVisible={modalVisible}
                    />
                    <div className="press-game__performance">
                        <PerformanceTracker
                            currentPerformance={currentPerformance}
                            maxPerformance={20}
                            game="press"
                        />
                        <Paragraph fontWeight="bold" withSpacing={false}>
                            Performance
                        </Paragraph>
                    </div>
                </div>
                <canvas
                    ref={initialiseCanvas}
                    id="pressCanvas"
                    className="press-game__canvas"
                    height="600"
                    width="1200"
                />
            </main>
            {!tutorialCompleted && (
                <TutorialModalParent
                    screen="press"
                    onClick={() => completePressTutorial()}
                />
            )}
            {tutorialCompleted && !settingsChosen && (
                <GameSettingsModalParent
                    screen="press"
                    onClick={() => setSettingsChosen(true)}
                    onRadioSelect={e => {
                        setSettingsOptionChosen(e.target.value);
                    }}
                    checked={settingsOptionChosen}
                    buttonIsDisabled={gameTimer === null}
                />
            )}
            {progressUpdateVisible === true && (
                <ProgressUpdateModalParent
                    previousScores={previousScores}
                    gameScores={currentGameScores}
                    feedback={responseFeedback}
                    onClick={() => completeGame()}
                />
            )}
        </>
    );
};

const mapStateToProps = state => {
    return {
        history: state.router.history,
        continueRoute: state.router.history[state.router.history.length - 2],
        historyScreenState: state.router.historyScreenState,
        tutorialCompleted:
            state.activeGame.tutorialsCompleted[PRESS_GAME_SCREEN],
        previousScores: state.activeGame.overallScores,
        timedGames: state.settings.accessibility.timedGames,
    };
};

const mapDispatchToProps = dispatch => {
    return {
        goToSettings: (
            currentPerformance,
            timeLeft,
            noTimer,
            activeJournalists,
            settingsChosen,
            timerLength,
        ) => {
            dispatch(
                setHistoryScreenState(PRESS_GAME_SCREEN, {
                    currentPerformance,
                    timeLeft,
                    noTimer,
                    activeJournalists,
                    settingsChosen,
                    timerLength,
                }),
            );
            dispatch(goToScreen(SETTINGS_SCREEN));
        },
        continueTo: route => {
            dispatch(goToScreenAndReset(route));
        },
        completePressTutorial: () => {
            dispatch(completeTutorial(PRESS_GAME_SCREEN));
        },
        submitOverallScores: (previousScores, gameScores) => {
            dispatch(updateOverallScores(previousScores, gameScores));
        },
        progressGame: () => {
            dispatch(progressTime('press'));
        },
    };
};

const connected = connect(mapStateToProps, mapDispatchToProps)(PressGameScreen);
export { connected as PressGameScreen };
